/**
 * 課金ゲート v4.2.0 — 伝票数で決める
 *
 * 【なぜ端末で分けるのをやめたか】
 * 以前は「PC だけなら無料 / アプリを使うと有料」にしていたが、
 *   - スマホのブラウザで PC 画面を開けば抜けられる（塞ぐ手段がない）
 *   - どの端末を使うかは店の規模と関係ない
 *   - 「PC 1台のスナック」と「PC 1台の大箱」が同じ扱いになる
 * 伝票数（＝会計の件数）なら、規模にそのまま比例し、端末を問わない。
 * 会計するたびに /checkouts が1件できるので、そのまま数えられる。
 *
 * 【実測（2ヶ月シミュレーション）】
 *   10名スナック   月あたり 約137件
 *   30名メンパブ   月あたり 約425件
 *   80名キャバ     月あたり 約1,050件
 * この分布に合わせて段を切ってある。
 */

export type PlanTier = 'free' | 'standard' | 'pro';

/** 無料で使える月あたりの伝票数。小さいスナックが収まる線。 */
export const FREE_SLIP_LIMIT = 200;
/** スタンダードの上限 */
export const STANDARD_SLIP_LIMIT = 1500;
/**
 * プロの上限。
 *
 * 「上限なし」にしていたが、商品カテゴリを A店舗・B店舗 のように使えば
 * 1契約で何店舗でも回せてしまう。それだと段の意味が無くなるので上限を置く。
 * 5,000 は、大箱（80名クラス）1店で月1,700件前後という実測に対して
 * 3倍近い余裕がある線。
 */
export const PRO_SLIP_LIMIT = 5000;

export interface PlanLimits {
  /** 月あたりの伝票数の上限。null = 無制限 */
  maxSlipsPerMonth: number | null;
  /** 従業員数の上限。null = 無制限（端末では縛らない） */
  maxEmployees: number | null;
  /** 複数店舗 */
  multiShop: boolean;
  /** 使える機能。夜職に必要なものは無料でも全部開ける。 */
  features: {
    staffApp: boolean;
    tableSessions: boolean;
    incentives: boolean;
    allowances: boolean;
    ledgers: boolean;
    analytics: boolean;
    payroll: boolean;
  };
}

const ALL_FEATURES: PlanLimits['features'] = {
  staffApp: true, tableSessions: true, incentives: true,
  allowances: true, ledgers: true, analytics: true, payroll: true,
};

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  // 機能は絞らない。絞ると「安いから金額が合わない」状態を作ってしまう。
  // 数が増えたら払ってもらう、という線引きにする。
  free: { maxSlipsPerMonth: FREE_SLIP_LIMIT, maxEmployees: null, multiShop: false, features: ALL_FEATURES },
  standard: { maxSlipsPerMonth: STANDARD_SLIP_LIMIT, maxEmployees: null, multiShop: false, features: ALL_FEATURES },
  pro: { maxSlipsPerMonth: PRO_SLIP_LIMIT, maxEmployees: null, multiShop: true, features: ALL_FEATURES },
};

export const PLAN_LABELS: Record<PlanTier, string> = {
  free: '無料', standard: 'スタンダード', pro: 'プロ',
};

export interface ShopUsage {
  /** 当月の伝票数（会計の件数） */
  slipsThisMonth: number;
  /** 直近3ヶ月の月あたり最大。単月のブレで課金が上下しないようにする */
  peakMonthlySlips?: number;
  employeeCount: number;
  shopCount: number;
}

export interface GateResult {
  /** 実際に適用される段 */
  tier: PlanTier;
  limits: PlanLimits;
  /** 上限に対する使用率（0-1）。null = 無制限 */
  usageRatio: number | null;
  /** 上限を超えているか */
  overLimit: boolean;
  /** あと何件で上限か。null = 無制限 */
  remaining: number | null;
  /** 画面に出す説明 */
  message: string;
  /** 次の段に上げるべきか */
  suggestUpgrade: PlanTier | null;
}

/** 課金判定に使う件数。単月のブレを吸収するため直近のピークを見る。 */
export function billingSlips(usage: ShopUsage): number {
  const now = Number(usage?.slipsThisMonth);
  const peak = Number(usage?.peakMonthlySlips ?? 0);
  const n = Math.max(
    Number.isFinite(now) ? now : 0,
    Number.isFinite(peak) ? peak : 0
  );
  // 数えられなかったときに最上位の段へ倒れると、使っていない店に請求が立つ。
  // 分からないときは 0 件として扱い、無料側に倒す。
  return n < 0 ? 0 : n;
}

/** 伝票数から必要な段を出す */
export function requiredTier(usage: ShopUsage): PlanTier {
  if ((usage?.shopCount ?? 1) > 1) return 'pro';
  const n = billingSlips(usage);
  if (n <= FREE_SLIP_LIMIT) return 'free';
  if (n <= STANDARD_SLIP_LIMIT) return 'standard';
  // プロにも上限がある。超えた場合もプロを返す（この上の段は無い）。
  // 会計は止めないので、案内文で相談を促す。
  return 'pro';
}

/**
 * 契約している段と、実際の使用量から状態を出す。
 * 契約が無ければ使用量から自動で決める。
 */
export function evaluatePlan(
  contractedTier: PlanTier | undefined,
  usage: ShopUsage
): GateResult {
  const needed = requiredTier(usage);
  const tier = contractedTier ?? needed;
  const limits = PLAN_LIMITS[tier];
  const n = billingSlips(usage);
  const max = limits.maxSlipsPerMonth;

  const overLimit = max !== null && n > max;
  const remaining = max === null ? null : Math.max(0, max - n);
  const usageRatio = max === null ? null : Math.min(1, n / max);

  // n は当月とピークの大きいほう（billingSlips）。
  // これを「当月」と書くと、当月71件なのに「当月1,698件」と出て
  // 「そんなに切っていない」と食い違う。何を見た数字かを書く。
  const basis = n > usage.slipsThisMonth ? '直近3ヶ月の最大' : '当月';
  const detail =
    n > usage.slipsThisMonth
      ? `（当月 ${usage.slipsThisMonth.toLocaleString()} 件／直近3ヶ月の最大 ${n.toLocaleString()} 件）`
      : `（当月 ${n.toLocaleString()} 件）`;

  let message: string;
  if (max === null) {
    message = `伝票数の上限はありません${detail}。`;
  } else if (overLimit && tier === 'pro') {
    // プロの上より上の段は無い。乗り換え先を案内できないので、
    // 「変更が必要です」とは書かない。会計は止まらないことを先に伝える。
    message =
      `${basis}の伝票が ${n.toLocaleString()} 件で、${PLAN_LABELS[tier]}の上限 ` +
      `${max.toLocaleString()} 件を超えています。会計は止まりません。` +
      `店舗数に合わせたご案内をしますので、ご相談ください。`;
  } else if (overLimit) {
    message =
      `${basis}の伝票が ${n.toLocaleString()} 件で、${PLAN_LABELS[tier]}の上限 ${max.toLocaleString()} 件を超えています。` +
      `${PLAN_LABELS[needed]}への変更が必要です。`;
  } else if (usageRatio !== null && usageRatio >= 0.8) {
    message =
      `${basis}の伝票 ${n.toLocaleString()} 件（上限 ${max.toLocaleString()} 件）。` +
      `残り ${remaining!.toLocaleString()} 件で上限に届きます。`;
  } else {
    message = `${basis}の伝票 ${n.toLocaleString()} 件（上限 ${max.toLocaleString()} 件）。${PLAN_LABELS[tier]}で足りています。`;
  }

  if (usage.shopCount > 1 && tier !== 'pro') {
    message += ` 店舗が ${usage.shopCount} 件あるためプロが必要です。`;
  }

  return {
    tier, limits, usageRatio, overLimit, remaining, message,
    suggestUpgrade: needed !== tier && rank(needed) > rank(tier) ? needed : null,
  };
}

function rank(t: PlanTier): number {
  return t === 'free' ? 0 : t === 'standard' ? 1 : 2;
}

export interface LimitCheck {
  allowed: boolean;
  message?: string;
  suggestTier?: PlanTier;
}

/**
 * 会計を1件立てられるか。
 *
 * 上限を超えても会計そのものは止めない。
 * 営業中に「上限です」で会計できなくなるのは、店にとって事故そのもの。
 * 超過は警告として出し、請求で精算する。
 */
export function canCreateSlip(gate: GateResult): LimitCheck {
  if (!gate.overLimit) return { allowed: true };
  // どの段でも会計は止めない。お客様を待たせないため。
  if (gate.tier === 'pro') {
    // プロの上に段は無い。「上位プランへの変更が必要」と出すと、
    // 存在しない選択肢を案内することになる。
    return {
      allowed: true,
      message:
        `${PLAN_LABELS[gate.tier]}の伝票数の上限を超えています。営業は続けられます。` +
        `店舗数に合わせたご案内をしますので、ご相談ください。`,
    };
  }
  return {
    allowed: true,
    message:
      `${PLAN_LABELS[gate.tier]}の伝票数の上限を超えています。営業は続けられますが、` +
      `${gate.suggestUpgrade ? PLAN_LABELS[gate.suggestUpgrade] : '上位プラン'}への変更が必要です。`,
    suggestTier: gate.suggestUpgrade ?? undefined,
  };
}

/** 複数店舗を持てるか */
export function canAddShop(gate: GateResult): LimitCheck {
  if (gate.limits.multiShop) return { allowed: true };
  return {
    allowed: false,
    message: '複数店舗の管理はプロプランで使えます。',
    suggestTier: 'pro',
  };
}

/**
 * 段ごとの月額（税抜・円）。
 * ここが金額の唯一の出どころ。料金表もLPもこの値から書く。
 * 2箇所に同じ数字を置くと、片方だけ変わっても誰も気づけない。
 */
export const TIER_MONTHLY_PRICE: Record<PlanTier, number> = {
  free: 0,
  standard: 9800,
  pro: 19800,
};

/**
 * 私たちが店から受け取る月額の消費税率。
 *
 * taxRate.ts は「店がお客様から預かる税」の入口で、店ごとに変えられる。
 * こちらは「私たちが店から受け取る税」なので別物。混ぜてはいけない。
 * 店の設定を変えると私たちへの請求額まで動く、という事故になる。
 */
export const PLATFORM_TAX_RATE = 0.1;

/**
 * 段ごとの月額（税込・円）。Stripe に登録する金額はこれ。
 *
 * 日本の消費税は 1 円未満を切り捨てる。
 * 9,800 × 1.1 = 10,780 / 19,800 × 1.1 = 21,780 なので端数は出ないが、
 * 税率が変わったときに切り上げてしまわないよう Math.floor を通す。
 */
export function monthlyPriceIncludingTax(tier: PlanTier): number {
  return Math.floor(TIER_MONTHLY_PRICE[tier] * (1 + PLATFORM_TAX_RATE));
}

/** 料金表に出す段の説明 */
export const TIER_DESCRIPTIONS: Record<PlanTier, { slips: string; price: string; note: string }> = {
  free: {
    slips: `月 ${FREE_SLIP_LIMIT} 伝票まで`,
    price: '無料',
    note: 'カウンター中心の小さな店。機能の制限はありません。',
  },
  standard: {
    slips: `月 ${STANDARD_SLIP_LIMIT.toLocaleString()} 伝票まで`,
    price: `月額 ${TIER_MONTHLY_PRICE.standard.toLocaleString()} 円`,
    note: 'メンズパブ・小箱クラスまで。機能は無料と同じです。',
  },
  pro: {
    slips: `月 ${PRO_SLIP_LIMIT.toLocaleString()} 伝票まで`,
    price: `月額 ${TIER_MONTHLY_PRICE.pro.toLocaleString()} 円`,
    note: '大箱・複数店舗。店舗をまたいだ管理ができます。',
  },
};
